/**
 * kotPrintRetry.ts — Impression KOT avec retry actionnable (lot 4 chantier CHR).
 *
 * Enrobe PrintingApi.printKitchenTicket : en cas d'échec, affiche un toast
 * warning avec action « Réessayer » qui relance l'impression AVEC LA MÊME CLÉ
 * d'idempotence. Grâce à kot_print_jobs (persistence + claim des jobs failed),
 * le retry ne ré-imprime que les groupes en échec — jamais les bons déjà sortis.
 *
 * Invariants :
 *   4.A.15 — aucun PII (payload KOT = produits/quantités/notes prépa)
 *   4.B.2  — aucun import supabase (passe par PrintingApi)
 *   4.B.4  — 0 any
 */

import { toast } from 'sonner';
import { isErr } from '@/api/result';
import { PrintingApi } from '@/api/printing';
import type { KitchenTicketPayload } from '@/api/printing';
import { kotFailureId, kotFailures } from './printFailureStore';
import { supportsAsyncConnect } from '@/services/hardware/printer/bt/connectAsync';

/** Nouvelles tentatives seules : l'imprimante cuisine peut mettre du temps à se réveiller. */
export const KOT_AUTO_RETRY_DELAYS_MS = [5_000, 15_000] as const;

export interface KotPrintRetryParams {
  tenantId: string;
  payload: KitchenTicketPayload;
  registerId?: string;
  /** Cible du picker imprimante (optionnel). */
  printerId?: string;
  /**
   * Clé d'idempotence persistante — DOIT être unique par envoi (suffixe horodaté).
   * Absente (flag chr_kot_idempotence OFF) : impression directe #234 — le toast
   * « Réessayer » réimprime alors le bon entier, sans garantie anti-double.
   */
  idempotencyKey?: string;
  /** Libellé du bon dans les toasts (« cuisine », « bar »). Défaut : « cuisine ». */
  label?: string;
}

/** Cause lisible pour l'équipe (avant : code brut « format_error », « no_printer_connected »). */
function failureHint(type: string): string {
  if (type === 'no_printer_connected') return 'imprimante injoignable ou éteinte';
  return 'erreur d’impression, vérifiez l’imprimante';
}

const printOnce = ({
  tenantId,
  payload,
  registerId,
  printerId,
  idempotencyKey,
}: KotPrintRetryParams) =>
  PrintingApi.printKitchenTicket(tenantId, payload, registerId, printerId, idempotencyKey);

/**
 * Seulement sans choix manuel d'imprimante (les bons déjà sortis sont alors ignorés) et
 * avec la connexion Bluetooth non bloquante : sur une ancienne APK, chaque essai figerait l'écran.
 */
function canRetryAlone(params: KotPrintRetryParams): boolean {
  const bridge = (window as unknown as { VisionPosBT?: { connectAsync?: unknown } }).VisionPosBT;
  return !params.printerId && !!bridge && supportsAsyncConnect(bridge as { connectAsync?: never });
}

async function attempt(params: KotPrintRetryParams, isRetry: boolean): Promise<boolean> {
  const { label = 'cuisine' } = params;
  let result = await printOnce(params);
  if (isErr(result) && !isRetry && canRetryAlone(params)) {
    for (const delay of KOT_AUTO_RETRY_DELAYS_MS) {
      await new Promise((resolve) => window.setTimeout(resolve, delay));
      result = await printOnce(params);
      if (!isErr(result)) break;
    }
  }
  if (!isErr(result)) {
    kotFailures.resolve(kotFailureId(params));
    if (isRetry) toast.success(`Bon ${label} imprimé`, { icon: '🖨️' });
    return true;
  }
  kotFailures.push(params);
  toast.warning(`Bon ${label} non imprimé — ${failureHint(result.error.type)}`, {
    duration: 10_000,
    action: {
      label: 'Réessayer',
      onClick: () => {
        void attempt(params, true);
      },
    },
  });
  return false;
}

/**
 * Imprime un KOT ; sur échec, toast « Réessayer » (même clé → seuls les
 * groupes non imprimés repartent). Retourne true si l'impression initiale
 * a réussi — les retries suivants notifient par toast.
 */
export function printKotWithRetryToast(params: KotPrintRetryParams): Promise<boolean> {
  return attempt(params, false);
}
